/**
 * Alert Redux Slice
 * 
 * Manages global alert/notification state for the application.
 * Allows any component to show alerts via Redux actions.
 */

import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  alerts: [], // Array of active alerts
};

const alertSlice = createSlice({
  name: "alert",
  initialState,
  reducers: {
    // Show a new alert
    showAlert: (state, action) => {
      const { type, message, messages, duration = 5000, position = "top-right" } = action.payload;
      const id = Date.now() + Math.random(); // Unique ID
      
      state.alerts.push({
        id,
        type: type || "info",
        message: message || null,
        messages: messages || null,
        duration,
        position,
        timestamp: Date.now(),
      });
    },
    
    // Show success alert
    showSuccess: (state, action) => {
      const { message, messages, duration, position } = action.payload;
      const id = Date.now() + Math.random();
      
      state.alerts.push({
        id,
        type: "success",
        message: message || null,
        messages: messages || null,
        duration: duration || 5000,
        position: position || "top-right",
        timestamp: Date.now(),
      });
    },
    
    // Show error alert
    showError: (state, action) => {
      const { message, messages, duration, position } = action.payload;
      const id = Date.now() + Math.random();
      
      state.alerts.push({
        id,
        type: "danger",
        message: message || null,
        messages: messages || null,
        duration: duration || 7000, // Errors stay longer
        position: position || "top-right",
        timestamp: Date.now(),
      });
    },
    
    // Show warning alert
    showWarning: (state, action) => {
      const { message, messages, duration, position } = action.payload;
      const id = Date.now() + Math.random();
      
      state.alerts.push({
        id,
        type: "warning",
        message: message || null,
        messages: messages || null,
        duration: duration || 6000,
        position: position || "top-right",
        timestamp: Date.now(),
      });
    },
    
    // Show info alert
    showInfo: (state, action) => {
      const { message, messages, duration, position } = action.payload;
      const id = Date.now() + Math.random();
      
      state.alerts.push({
        id,
        type: "info",
        message: message || null,
        messages: messages || null,
        duration: duration || 5000,
        position: position || "top-right",
        timestamp: Date.now(),
      });
    },
    
    // Remove a specific alert
    removeAlert: (state, action) => {
      state.alerts = state.alerts.filter(alert => alert.id !== action.payload);
    },
    
    // Clear all alerts
    clearAlerts: (state) => {
      state.alerts = [];
    },
  },
});

export const {
  showAlert,
  showSuccess,
  showError,
  showWarning,
  showInfo,
  removeAlert,
  clearAlerts,
} = alertSlice.actions;

export default alertSlice.reducer;

// Selectors
export const selectAlerts = (state) => state.alert?.alerts || [];
export const selectAlertsByPosition = (position) => (state) => 
  (state.alert?.alerts || []).filter(alert => alert.position === position);

